import { Mesh, Vector2, type Color, type Material, type Node, type Object3D } from 'three/webgpu';
import {
  abs,
  dot,
  log,
  materialEmissive,
  max,
  mix,
  mx_fractal_noise_float,
  screenSize,
  screenUV,
  select,
  smoothstep,
  step,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';

import { COLORS } from '../core/constants';

/**
 * Vertical pitch of the hex lattice. The tiling is two rectangular grids offset
 * against each other, and `sqrt(3)` is the row spacing that makes the nearer of
 * the two centres always a hexagon's worth of ground.
 */
const PITCH = 1.7320508;

/** Half the row spacing: how far apart the interleaved rows actually sit. */
const ROW = PITCH / 12;

/** Normal of a hex's slanted edge, used to measure the distance out to the rim. */
const EDGE_NORMAL = vec2(1, PITCH).normalize();

/** Keeps the direction out of the origin defined for the fragment sitting on it. */
const MIN_RADIUS = 1e-4;

export interface BurnOptions {
  /**
   * Roughly how many hex rows fit down the height of the screen — at `origin`,
   * once `growth` has the cells changing size across the frame.
   */
  density?: number;
  /**
   * How many times wider a cell out at the furthest corner is than one sitting
   * on `origin`. `1` is an even lattice; above that the cells start out small
   * under the origin and open up as they get away from it.
   */
  growth?: number;
  /** How many times longer a cell is along its own axis than across it. */
  stretch?: number;
  /** Degrees the whole lattice is turned by. */
  angle?: number;
  /** How coarse the noise is relative to the cells. */
  grain?: number;
  /** `0` is a clean directional wipe, `1` lets the noise decide the whole order. */
  turbulence?: number;
  /** How far into a cell the front eats before the cell is gone entirely. */
  softness?: number;
  /** Width of the glowing rim, in the same units as the progress sweep. */
  edge?: number;
  /** Sweep out from a point on the screen rather than along `sweep`. */
  radial?: boolean;
  /**
   * Where a radial sweep starts from, in screen UV — `[0.5, 0.5]` is the
   * middle, and `y` runs from the top down. Move it later through `origin`.
   */
  origin?: [number, number];
  /**
   * Screen-space direction the front travels in. Screen UV runs from the top
   * down, so `[0, 1]` sweeps downward and `[0, -1]` climbs.
   */
  sweep?: [number, number];
  /** Colour of the rim. Driven above white so it crosses the bloom threshold. */
  colour?: Color;
  /** How far above white the rim is driven. */
  gain?: number;
  /** Shifts the noise field, so two burns running together don't rhyme. */
  seed?: number;
}

export interface Burn {
  /** `0` is nothing on screen, `1` is whole. Drive it over time either way. */
  progress: { value: number };
  /**
   * Where a radial burn opens out of and collapses back into, in screen UV.
   *
   * Moving it re-arranges which cells are present, which passes unseen while
   * the burn is whole and reads as a jolt while it is part way — so a burn
   * that is still running wants this eased rather than set.
   */
  origin: { value: Vector2 };
  /**
   * Wires the burn into every material under `root`. Safe on classic materials
   * as well as node ones: the renderer copies node properties across when it
   * converts a classic material for the node pipeline.
   *
   * Call this from a layout effect and pair it with `dispose` in that effect's
   * cleanup. It mutates materials that belong to a cached, shared model, and
   * React mounts a component twice in development — applying during render
   * instead leaves the second mount with the materials stripped bare.
   */
  applyTo(root: Object3D): void;
  /** Puts every material this burn touched back the way it was. */
  dispose(): void;
}

interface Burnable {
  opacityNode?: Node | null;
  emissiveNode?: Node | null;
  /** Present on lit materials; the unlit display has no emissive to keep. */
  emissive?: Color;
  alphaTest: number;
  needsUpdate: boolean;
}

/**
 * A hexagonal dissolve: a front travels through the `hexField` lattice and the
 * cells behind it are cut away, with the front itself lit like a burning edge.
 *
 * The cells are laid out in screen space rather than on the surface. That keeps
 * them regular hexagons on every piece of geometry regardless of how it faces
 * the camera — the alternative, projecting onto the object, smears them across
 * anything side-on — at the cost of the pattern swimming a little under
 * geometry that moves quickly while it burns.
 *
 * One burn drives one set of materials in both directions: run `progress` up to
 * reveal, down to burn away.
 */
/** A point on the screen the lattice is laid out around, in screen UV. */
export function screenOrigin(x: number, y: number) {
  return uniform(new Vector2(x, y));
}

export type ScreenOrigin = ReturnType<typeof screenOrigin>;

export interface LatticeOptions {
  density: number;
  growth: number;
  stretch: number;
  angle: number;
  grain: number;
  turbulence: number;
  softness: number;
  radial: boolean;
  sweep: [number, number];
  seed: number;
}

export const LATTICE_DEFAULTS: LatticeOptions = {
  density: 22,
  growth: 1,
  stretch: 2,
  angle: -45,
  grain: 0.22,
  turbulence: 0.55,
  softness: 0.35,
  radial: false,
  sweep: [0, 1],
  seed: 0,
};

/**
 * The hex lattice and the field a front sweeps through it, built around an
 * `origin` uniform in screen UV.
 *
 * The frame is cut into hex cells, an FBM sample per cell decides when that
 * cell's turn comes, and a gradient decides which way the front travels
 * overall. `field` runs from 0 at the start of the sweep to a little past 1 at
 * the end, and rises toward each cell's rim, so a front moving through it eats
 * each cell from the middle out.
 */
export function hexField(origin: ScreenOrigin, options: LatticeOptions) {
  const { density, growth, stretch, angle, grain, turbulence, softness, radial, sweep, seed } =
    options;

  // Centred and aspect-corrected, so the cells stay regular whatever shape the
  // window is: x runs to ±aspect/2, y to ±0.5.
  const aspect = screenSize.x.div(screenSize.y);
  const point = screenUV.sub(0.5).mul(vec2(aspect, 1));

  // The same frame, measured out from the origin rather than the middle.
  const fromOrigin = screenUV.sub(origin).mul(vec2(aspect, 1));
  const distance = fromOrigin.length();

  // Distance from the origin out to the furthest corner. It sets how far the
  // sweep has to travel, and it is what `growth` is quoted against, so both
  // read the same whatever shape the window is.
  const reach = vec2(
    max(origin.x, origin.x.oneMinus()).mul(aspect),
    max(origin.y, origin.y.oneMinus()),
  ).length();

  // Cells that open up with distance: the lattice is laid out on a
  // logarithmically stretched copy of the screen, which is the mapping whose
  // scale climbs steadily with the radius — so the cells simply grow rather
  // than being smeared into rings the way a plain radial scaling leaves them.
  // `core` is the radius at which a cell has doubled.
  const core = reach.div(Math.max(growth - 1, MIN_RADIUS));
  const stretched = core.mul(log(distance.div(core).add(1)));
  const spaced = fromOrigin.mul(stretched.div(max(distance, MIN_RADIUS)));

  // Around the origin rather than the middle of the screen, which is what puts
  // the smallest cells under the point the burn runs from.
  const base = growth > 1 ? spaced : point;

  // The lattice is laid out in a frame of its own: turned, then stretched along
  // that frame's x axis, which comes out as elongated cells leaning over. Only
  // the tiling moves — the sweep below still reads the upright screen, so the
  // front travels the way it is asked to whatever the cells are doing.
  const turn = (angle * Math.PI) / 180;
  const turned = vec2(
    base.x.mul(Math.cos(turn)).add(base.y.mul(Math.sin(turn))),
    base.y.mul(Math.cos(turn)).sub(base.x.mul(Math.sin(turn))),
  );
  const grid = turned.mul(vec2(1 / stretch, 1)).mul(density * ROW);

  // Two rectangular grids, the second offset by half a cell. Whichever centre
  // is nearer owns the fragment, and that is what makes the cells hexagons.
  const pitch = vec2(1, PITCH);
  const centreA = grid.div(pitch).floor().add(0.5);
  const centreB = grid.sub(vec2(0.5, 1)).div(pitch).floor().add(0.5);
  const localA = grid.sub(centreA.mul(pitch));
  const localB = grid.sub(centreB.add(0.5).mul(pitch));
  const nearer = dot(localA, localA).lessThan(dot(localB, localB));
  const local = select(nearer, localA, localB);
  const cell = select(nearer, centreA, centreB.add(0.5));

  // Distance from the middle of a cell out to its rim, as 0 to 1.
  const folded = abs(local);
  const rimDistance = max(dot(folded, EDGE_NORMAL), folded.x).mul(2);

  // One FBM sample per cell decides when that cell's turn comes; the gradient
  // decides which way the front travels overall. `turbulence` balances the two,
  // and the rim distance spreads each cell's turn across a moment rather than
  // switching it whole, so cells shrink toward their middles as they go.
  const noise = mx_fractal_noise_float(vec3(cell.mul(grain), seed), 3, 2, 0.5)
    .mul(0.5)
    .add(0.5);
  // Over the reach, so `progress` still runs from empty to whole wherever the
  // origin sits — off-centre, the near edge simply arrives before the far one.
  const gradient = radial
    ? distance.div(reach)
    : dot(point, vec2(sweep[0], sweep[1])).add(0.5);
  // Three octaves at half amplitude each sum to well past ±1, and the gradient
  // runs past its own ends off the sides of the screen. Holding the pair to 0..1
  // is what makes a `progress` of 0 and 1 honestly empty and whole rather than
  // leaving a few cells behind at either end.
  const field = mix(gradient, noise, turbulence).clamp(0, 1).add(rimDistance.mul(softness));

  return { field, distance, reach };
}

export function createBurn(options: BurnOptions = {}): Burn {
  const {
    edge = 0.12,
    origin: startOrigin = [0.5, 0.5],
    colour = COLORS.yellow,
    gain = 3,
  } = options;
  const { density, growth, stretch, angle, grain, turbulence, softness, radial, sweep, seed } = {
    ...LATTICE_DEFAULTS,
    ...options,
  };

  const progress = uniform(0);
  const origin = screenOrigin(startOrigin[0], startOrigin[1]);
  const { field } = hexField(origin, {
    density,
    growth,
    stretch,
    angle,
    grain,
    turbulence,
    softness,
    radial,
    sweep,
    seed,
  });

  // The front, swept far enough past both ends that `progress` of 0 and 1 are
  // genuinely empty and whole rather than nearly so.
  const front = progress.mul(1 + softness + edge).sub(edge);
  const present = step(field, front);

  // The rim rides just behind the front, and is faded out at either end of the
  // sweep so a finished burn isn't left with a lit outline.
  const settled = smoothstep(0, 0.06, progress).mul(smoothstep(0.94, 1, progress).oneMinus());
  const rim = smoothstep(front.sub(edge), front, field).mul(present).mul(settled);

  const glow = vec3(colour.r, colour.g, colour.b).mul(gain).mul(rim);
  // On top of the material's own emissive rather than in place of it, so a
  // button lit from within stays lit while it burns. Only where the material
  // has one: reading it off a material without breaks that material's uniforms.
  const emissive = materialEmissive.add(glow);

  const touched: { material: Burnable; opacityNode: Node | null; alphaTest: number }[] = [];

  return {
    progress,
    origin,

    applyTo(root) {
      // Collected off the model rather than handed in, so it is whatever is
      // actually being rendered right now — the instrument swaps in cloned
      // materials of its own, and a re-render swaps in fresh ones again.
      const materials = new Set<Material>();
      root.traverse((child) => {
        if (child instanceof Mesh) materials.add(child.material as Material);
      });

      for (const material of materials) {
        const target = material as unknown as Burnable;
        touched.push({
          material: target,
          opacityNode: target.opacityNode ?? null,
          alphaTest: target.alphaTest,
        });

        target.opacityNode = present;
        // The additively blended display has no emissive of its own and simply
        // burns without a rim.
        target.emissiveNode = target.emissive ? emissive : glow;
        // Discarding rather than blending keeps the cut depth-correct, so a
        // hole burnt through the front shows what is behind it.
        target.alphaTest = 0.5;
        target.needsUpdate = true;
      }
    },

    dispose() {
      for (const entry of touched) {
        entry.material.opacityNode = entry.opacityNode;
        entry.material.emissiveNode = null;
        entry.material.alphaTest = entry.alphaTest;
        entry.material.needsUpdate = true;
      }
      touched.length = 0;
    },
  };
}
